import { useMemo } from 'react';
import { connect } from 'react-redux';
import { Alert } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { AUTH_LEVELS } from '../../auth/Authorized';
import { defaultTableOptions, defaultTableInitialState } from '../../util/data-table';
import getBuildingStaging from '../../../redux/selectors/staging';
import {
  deviceColumns,
  deviceSuperColumn,
  RECENTLY_DISCOVERED_MS,
  deviceIdCol,
  deviceNameCol,
  deviceDescriptionCol,
  lastDiscoveredCol,
} from '../columns';

const Devices = ({
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  devicesLoadTime,
  stagingDevices,
  lastDiscovery,
}) => {
  const columns = useMemo(() => deviceColumns(deviceSuperColumn), []);

  // This breaks page changes while the page is rendering
  const data = useMemo(
    () =>
      stagingDevices.map((d) => ({
        ...d,
        // eslint-disable-next-line no-underscore-dangle
        address: d.properties?._address,
        pointCount: d.points.length ?? 0,
      })),
    [stagingDevices]
  );

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const devicesSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const devicesColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    enableGrouping: true,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: devicesColumnFilters,
        sorting: devicesSorting,
        // Defining columns we DO want to show by default (defaultTableInitialState will pull in filtered cols)
        hiddenColumns: columns
          .map((c) => c.id)
          .filter(
            (id) =>
              ![
                deviceSuperColumn.id,
                deviceIdCol().id,
                deviceNameCol().id,
                deviceDescriptionCol().id,
                'devicePointCount',
                lastDiscoveredCol.id,
              ].includes(id)
          ),
      }),
    },
    getRowId: (d) => d.id,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: devicesSorting,
      columnFilters: devicesColumnFilters,
      isLoading: !devicesLoadTime,
    },
  });

  return (
    <>
      {new Date() - lastDiscovery > RECENTLY_DISCOVERED_MS ? (
        <Alert severity="warning">
          At least one bacnet collection has not been discovered recently
        </Alert>
      ) : null}
      <MaterialReactTable table={mrt} />
    </>
  );
};

const mapStateToProps = (state, { building }) => {
  const {
    user: { userAuthLevel },
    entities: {
      staging: { devicesLoadTime },
      deployments: { deployments, discovery },
    },
  } = state;
  const { stagingDevices } = getBuildingStaging(state, building.id);
  const lastDiscovery = Math.min(
    ...Object.values(deployments)
      .filter((d) => d.buildings.includes(building.id))
      .flatMap((d) => discovery[d.site])
      .filter(Boolean)
      .map((disc) => disc.last_status_update)
  );
  return {
    devicesLoadTime: devicesLoadTime[building.id] ?? false,
    stagingDevices: [...stagingDevices.values()],
    isSuperAdmin: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
    canWrite: userAuthLevel < AUTH_LEVELS.ORG_USER,
    lastDiscovery: lastDiscovery === Infinity ? null : new Date(lastDiscovery),
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Devices);
